/**
 * Auth phía admin — access token giữ IN-MEMORY (chống XSS), không localStorage.
 * Token mất khi reload tab; khôi phục bằng silent refresh qua httpOnly refresh cookie.
 *
 * Silent refresh dùng POST /v1/admin/auth/refresh với `credentials:"include"` để gửi
 * httpOnly cookie `admin_refresh_token` (server set ở bước 2FA, scope path /v1/admin/auth).
 */
import { createTokenStore } from "@goppy/ui-kit/auth";
import { adminRoleSchema, type AdminRole } from "@goppy/contracts";
import { env } from "./env.js";
import { dbEnvHeaders, resetDbEnvToReal } from "./db-env.js";

const store = createTokenStore();

export function getToken(): string | null {
  return store.get();
}

export function setToken(token: string): void {
  store.set(token);
}

/**
 * Kết thúc phiên phía client: xoá token + admin hiện tại + kéo env dữ liệu về `real`.
 *
 * 🔴 US-AE-0321 (@UC9419): env `sandbox` persist ở localStorage nên nếu KHÔNG reset ở đây, nó sống
 * qua ranh giới phiên — admin kế tiếp (có thể là người khác trên cùng máy) vừa đăng nhập đã đọc DB
 * sandbox và tưởng là dữ liệu thật. Đặt ở `clearToken()` vì đây là nơi MỌI nhánh chết phiên đi qua
 * (logout + refresh thất bại), nên banner không kịp nháy trước khi RequireAuth đá về /login.
 */
export function clearToken(): void {
  store.clear();
  currentAdmin = null;
  resetDbEnvToReal();
}

/**
 * Admin hiện tại (id + role) — giữ IN-MEMORY cùng vòng đời token (không localStorage).
 * Dùng cho defense-in-depth UX (S04): ẩn/disable nút đổi vai khi không phải SUPER_ADMIN
 * và trên dòng của chính mình. KHÔNG phải chốt chặn — enforce thật ở backend (S01b).
 */
export interface CurrentAdmin {
  readonly adminMemberId: string;
  /**
   * Role hợp lệ (4-role enum) HOẶC undefined khi server trả role lạ/không tin được.
   * undefined ⇒ least privilege (consumer như MembersPage coi như KHÔNG quản trị) — M2 defense-in-depth.
   */
  readonly role?: AdminRole;
  /**
   * Email admin đang đăng nhập (US-AE-IMP.F1-0110-S02 · @UC9135) — footer sidebar hiển thị định danh
   * THẬT thay hash UUID. Vắng (server không trả / kiểu lạ) ⇒ footer fallback "Admin", KHÔNG hash.
   */
  readonly email?: string;
}

let currentAdmin: CurrentAdmin | null = null;

export function getCurrentAdmin(): CurrentAdmin | null {
  return currentAdmin;
}

export function setCurrentAdmin(admin: CurrentAdmin): void {
  currentAdmin = admin;
}

export function clearCurrentAdmin(): void {
  currentAdmin = null;
}

/**
 * Silent refresh: đổi httpOnly refresh cookie lấy access token mới. Gọi khi app khởi động,
 * theo lịch trước khi token hết hạn, và khi gặp 401. Trả token mới (đã set vào store) hoặc null
 * nếu phiên không còn hợp lệ (clear store khi thất bại). Không log token/secret.
 *
 * SINGLE-FLIGHT (bắt buộc): endpoint refresh là rotation SINGLE-USE — mỗi lần gọi thành công sẽ
 * revoke refresh token cũ và phát cookie mới. Nếu N request 401 cùng lúc và mỗi cái tự gọi refresh,
 * cái đầu rotate xong thì N-1 cái còn lại trình token đã bị revoke → server coi là REUSE-DETECTION,
 * revoke cả family → user bị đá ra khỏi phiên. Vì vậy mọi lời gọi đồng thời PHẢI dùng chung một
 * promise đang bay; chỉ khi nó settle mới cho phép lượt refresh kế tiếp.
 */
let inFlightRefresh: Promise<string | null> | null = null;

export function refreshToken(): Promise<string | null> {
  // Đã có một refresh đang bay → dùng chung, KHÔNG bắn request thứ hai.
  if (inFlightRefresh) return inFlightRefresh;

  const run = async (): Promise<string | null> => {
    try {
      const res = await fetch(`${env.apiBaseUrl}/admin/auth/refresh`, {
        method: "POST",
        credentials: "include",
      });
      if (!res.ok) {
        clearToken();
        return null;
      }
      const body = (await res.json()) as {
        token?: unknown;
        admin?: { id?: unknown; role?: unknown; email?: unknown };
      };
      if (typeof body.token !== "string" || body.token.length === 0) {
        clearToken();
        return null;
      }
      setToken(body.token);
      // US-AE.F9-0100 BUG-6: refresh PHẢI khôi phục cả currentAdmin (id+role) như login — nếu không,
      // sau boot silent-refresh getCurrentAdmin()=null → UI RBAC (nút Add dict, đổi vai…) ẩn oan với
      // SUPER_ADMIN. Validate role qua adminRoleSchema (role lạ → undefined = least privilege, M2).
      if (body.admin && typeof body.admin.id === "string") {
        const parsedRole = adminRoleSchema.safeParse(body.admin.role);
        setCurrentAdmin({
          adminMemberId: body.admin.id,
          role: parsedRole.success ? parsedRole.data : undefined,
          // @UC9135: kèm email (chỉ khi là string) để footer khôi phục định danh sau boot-refresh.
          ...(typeof body.admin.email === "string" ? { email: body.admin.email } : {}),
        });
      }
      return body.token;
    } catch {
      clearToken();
      return null;
    }
  };

  // Gán TRƯỚC khi await để lời gọi đồng bộ kế tiếp thấy ngay promise đang bay.
  inFlightRefresh = run().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

/**
 * Đọc hạn (`exp`, giây epoch) từ payload JWT mà KHÔNG verify chữ ký — client chỉ dùng để biết
 * *khi nào* nên refresh, không dùng để tin quyền (quyền enforce ở backend). Token lạ/hỏng → null.
 */
function readTokenExpiryMs(token: string): number | null {
  const payload = token.split(".")[1];
  if (!payload) return null;
  try {
    const json = atob(payload.replace(/-/g, "+").replace(/_/g, "/"));
    const exp = (JSON.parse(json) as { exp?: unknown }).exp;
    return typeof exp === "number" ? exp * 1000 : null;
  } catch {
    return null;
  }
}

/**
 * Refresh CHỦ ĐỘNG trước khi access token hết hạn (TTL mặc định 10m).
 *
 * Vì sao cần: token giữ in-memory và trước đây chỉ refresh đúng một lần lúc boot. Tab mở lâu hơn
 * TTL thì poller nền (queues 60s, impersonation 30s) gặp token hết hạn → 401 → màn "Unauthorized",
 * chỉ hết khi hard-reload (reload chạy lại boot-refresh). Đặt lịch theo `exp` THẬT của token, trừ
 * biên an toàn, để token luôn còn hạn khi request rời trình duyệt.
 */
const REFRESH_SKEW_MS = 60_000;
/** Sàn chống lịch dồn: token sắp/đã hết hạn → refresh sau 5s, không tự gọi đệ quy tức thì. */
const MIN_REFRESH_DELAY_MS = 5_000;

let refreshTimer: ReturnType<typeof setTimeout> | null = null;

/** Huỷ lịch refresh (logout / phiên chết). */
export function cancelScheduledRefresh(): void {
  if (refreshTimer !== null) {
    clearTimeout(refreshTimer);
    refreshTimer = null;
  }
}

/**
 * Đặt lịch refresh kế tiếp theo hạn của token hiện tại. Gọi lại sau MỖI lần refresh thành công để
 * chuỗi tự nối. Không có token / token không đọc được `exp` → không đặt lịch (fallback: 401-retry
 * trong authedFetch vẫn cứu được).
 */
export function scheduleTokenRefresh(): void {
  cancelScheduledRefresh();
  const token = getToken();
  if (!token) return;
  const expiryMs = readTokenExpiryMs(token);
  if (expiryMs === null) return;

  const delay = Math.max(expiryMs - Date.now() - REFRESH_SKEW_MS, MIN_REFRESH_DELAY_MS);
  refreshTimer = setTimeout(() => {
    void refreshToken().then((fresh) => {
      // Thành công → nối lịch cho token mới. Thất bại → phiên đã chết, dừng (RequireAuth lo redirect).
      if (fresh) scheduleTokenRefresh();
    });
  }, delay);
}

/**
 * fetch có xác thực cho MỌI lời gọi /v1/admin/* — nguồn sự thật duy nhất của vòng đời token phía FE.
 *
 * Ba bảo đảm:
 *  1. Chưa có token nhưng còn refresh cookie → refresh TRƯỚC, không bắn request trần (thiếu
 *     Authorization) rồi ăn 401 oan (đua boot: xem useBootstrapAuth).
 *  2. Gặp 401 → refresh (single-flight) rồi RETRY đúng MỘT lần với token mới.
 *  3. Retry vẫn 401, hoặc refresh hỏng → trả nguyên response 401 cho caller. KHÔNG lặp vô hạn.
 *
 * 403 (RBAC) KHÔNG kích hoạt refresh: token vẫn hợp lệ, chỉ là thiếu quyền — refresh vô ích.
 *
 * CHỈ dùng cho surface ĐÃ đăng nhập (sau RequireAuth). Trên trang công khai (vd /login) không có
 * refresh cookie, bước (1) sẽ tốn một POST /admin/auth/refresh chắc chắn 401 — vô hại (throttle
 * 120 req/phút) nhưng thừa. Trang công khai hãy gọi fetch trần.
 */
export async function authedFetch(path: string, init: RequestInit = {}): Promise<Response> {
  const send = (token: string | null): Promise<Response> => {
    const headers = new Headers(init.headers);
    if (token) headers.set("authorization", `Bearer ${token}`);
    // US-AIF.P0-0247 (@UC9419): env=sandbox ⇒ đính X-Admin-Db-Env cho MỌI GET admin qua authedFetch.
    for (const [k, v] of Object.entries(dbEnvHeaders())) headers.set(k, v);
    return fetch(`${env.apiBaseUrl}${path}`, { ...init, headers });
  };

  // (1) Không có token trong RAM (vừa reload / boot-refresh chưa xong) → thử khôi phục trước.
  let token = getToken();
  if (!token) token = await refreshToken();

  const res = await send(token);
  if (res.status !== 401) return res;

  // (2) 401: token hết hạn giữa chừng → refresh single-flight rồi retry đúng một lần.
  const fresh = await refreshToken();
  if (!fresh) return res; // (3) phiên chết thật → trả 401 gốc.

  scheduleTokenRefresh();
  return send(fresh);
}

/**
 * Logout: revoke session server-side (POST /admin/auth/logout xoá httpOnly refresh cookie) rồi
 * clear token + currentAdmin in-memory. Best-effort với backend — kể cả lỗi mạng vẫn clear local
 * để KHÔNG kẹt trạng thái đăng nhập giả. Caller điều hướng /login sau khi hàm này resolve.
 */
export async function logout(): Promise<void> {
  try {
    await fetch(`${env.apiBaseUrl}/admin/auth/logout`, {
      method: "POST",
      credentials: "include",
    });
  } catch {
    // Nuốt lỗi mạng: local vẫn phải clear để không "đăng nhập lại" khi reload.
  } finally {
    // Huỷ lịch refresh TRƯỚC khi clear: nếu không, timer còn sống sẽ tự dựng lại phiên đã đăng xuất.
    cancelScheduledRefresh();
    clearToken();
    clearCurrentAdmin();
  }
}
